import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, WalletIcon } from '@heroicons/react/16/solid'
import { PlusIcon } from '@heroicons/react/20/solid'
import { useLazyQuery } from '@apollo/client'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { Pagination, PaginationPrevious, PaginationList, PaginationPage, PaginationNext } from 'components/pagination'
import { SuccessDialog } from 'components/custom/ResponseDialog'
import loadingImage from '../../assets/images/boat-loading.gif'
import { GET_NAUTICAL_WALLET_TRANSACTIONS } from 'graphql/nauticalWallets/queries'
import NauticalTopUpDialog from './NauticalTopUpDialog'

export default function NauticalMilesWalletDetails() {
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()
    const userData = location.state?.userData
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [transactions, setTransactions] = useState([])
    const [walletInfo, setWalletInfo] = useState(null)
    const [transactionCount, setTransactionCount] = useState(0)
    const [showTopUp, setShowTopUp] = useState(false)
    const [showSuccessMessage, setShowSuccessMessage] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const totalPages = Math.ceil(transactionCount / rowsPerPage)
    
    const [getTransactions, { loading: transactionsLoading }] = useLazyQuery(GET_NAUTICAL_WALLET_TRANSACTIONS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getNauticalWalletTransactions?.success) {
                setTransactions(data.getNauticalWalletTransactions.tranx)
                setWalletInfo(data.getNauticalWalletTransactions.wallet)
                setTransactionCount(data.getNauticalWalletTransactions.tranx_count)
            }
        },
    })

    useEffect(() => {
        if (userData?.email) {
            getTransactions({
                variables: {
                    userEmail: userData.email,
                    page: {
                        offset: page * rowsPerPage,
                        limit: rowsPerPage,
                    },
                },
            })
        }
    }, [userData, page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const handleTopUpSuccess = (type, message) => {
        setSuccessMessage(message)
        setShowTopUp(false)
        setShowSuccessMessage(true)
        // Refresh wallet data
        getTransactions({
            variables: {
                userEmail: userData.email,
                page: {
                    offset: page * rowsPerPage,
                    limit: rowsPerPage,
                },
            },
        })
        setTimeout(() => {
            setShowSuccessMessage(false)
        }, 3000)
    }

    return (
        <>
            {/* Header section */}
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/nautical-miles-wallets/list')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
            </div>

            {/* Loading state */}
            {transactionsLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}

            {/* User Info and Wallet Balance Section */}
            {!transactionsLoading && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        {/* Wallet Balance Card with gradient */}
                        <div className="mt-4">
                            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#285f74] via-[#316c84] to-[#1d4757] p-8 shadow-xl">
                                {/* Pattern overlay */}
                                <div className="absolute inset-0 opacity-10">
                                    <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
                                        <defs>
                                            <pattern
                                                id="wallet-pattern"
                                                x="0"
                                                y="0"
                                                width="20"
                                                height="20"
                                                patternUnits="userSpaceOnUse"
                                            >
                                                <path d="M0 10h20v1H0zM10 0v20h1V0z" fill="currentColor" />
                                            </pattern>
                                        </defs>
                                        <rect width="100%" height="100%" fill="url(#wallet-pattern)" />
                                    </svg>
                                </div>

                                {/* Content */}
                                <div className="relative flex justify-between items-center">
                                    <div>
                                        <p className="text-base text-white/90">{t('Current Nautical Miles Balance')}</p>
                                        <h2 className="text-5xl font-bold text-white mt-2">
                                            {walletInfo?.balance || '0.00'} NM
                                        </h2>
                                        <p className="text-sm text-white/70 mt-2">
                                            {t('Hold Balance')}: {walletInfo?.hold_balance || '0.00'} NM
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowTopUp(true)}
                                        className="inline-flex items-center gap-x-2 rounded-md bg-white/10 backdrop-blur-sm px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-white/20 transition-all duration-200"
                                    >
                                        <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                        {t('Credit/Debit')}
                                    </button>
                                </div>

                                {/* Card bottom info */}
                                <div className="relative mt-4 pt-4 border-t border-white/10">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <p className="text-sm text-white/70 mb-1">{t('Name')}</p>
                                            <p className="text-base text-white">
                                                {userData?.user_info?.given_name && userData?.user_info?.family_name
                                                    ? `${userData?.user_info?.given_name} ${userData?.user_info?.family_name}`
                                                    : t('N/A')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-white/70 mb-1">{t('Email')}</p>
                                            <p className="text-base text-white">{userData?.email || t('N/A')}</p>
                                        </div>
                                        <div className="text-end">
                                            <p className="text-sm text-white/70 mb-1">{t('Phone number')}</p>
                                            <p className="text-base text-white">
                                                {userData?.user_info?.phone_number || t('N/A')}
                                            </p>
                                        </div>
                                    </div>
                                    {walletInfo?.last_updated && (
                                        <div className="mt-4 pt-4 border-t border-white/10">
                                            <p className="text-sm text-white/70">
                                                {t('Updated On')}: <span className="text-white">{new Date(walletInfo.last_updated).toLocaleString()}</span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Transactions Section */}
                        <div className="mt-8">
                            <h3 className="text-lg font-semibold mb-4">{t('Transaction History')}</h3>
                            <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-lg bg-white p-0 flex">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>{t('Date')}</TableHeader>
                                            <TableHeader>{t('Transaction ID')}</TableHeader>
                                            <TableHeader>{t('Distance (NM)')}</TableHeader>
                                            <TableHeader>{t('Description')}</TableHeader>
                                            <TableHeader>{t('Type')}</TableHeader>
                                            <TableHeader>{t('Status')}</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map(transaction => (
                                            <TableRow key={transaction.tranx_id}>
                                                <TableCell>
                                                    {new Date(transaction.tranx_date).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>{transaction.tranx_id}</TableCell>
                                                <TableCell>{transaction.nautical_mile}</TableCell>
                                                <TableCell className="max-w-xs break-words whitespace-normal">
                                                    <div className="break-words">{transaction.description}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge
                                                        color={transaction.tranx_type === 'CREDIT' ? 'emerald' : 'rose'}
                                                    >
                                                        {transaction.tranx_type}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge color="blue">{transaction.status}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Add Pagination */}
                            <Divider />
                            <Pagination className="py-6">
                                <PaginationPrevious
                                    onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                                    isDisabled={page === 0}
                                />
                                <PaginationList>
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <PaginationPage
                                            key={index}
                                            current={index === page}
                                            onClick={() => handlePageChange(index)}
                                        >
                                            {index + 1}
                                        </PaginationPage>
                                    ))}
                                </PaginationList>
                                <PaginationNext
                                    onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                                    isDisabled={page === totalPages - 1}
                                />
                            </Pagination>
                            <Divider />
                        </div>
                    </div>
                </div>
            )}

            {showTopUp && (
                <NauticalTopUpDialog
                    status={true}
                    onClose={() => setShowTopUp(false)}
                    onSuccess={handleTopUpSuccess}
                    userData={userData}
                />
            )}

            {showSuccessMessage && (
                <SuccessDialog
                    status={true}
                    title="Success"
                    message={successMessage}
                    onClose={() => setShowSuccessMessage(false)}
                />
            )}
        </>
    )
}